import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Search, Pencil, Eye, Star } from 'lucide-react';
import { PageLayout } from '@/components/ui/PageLayout';
import Icon from '@/components/AppIcon';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import Pagination from '@/components/Pagination';
import { cn } from '@/utils/cn';
import { useReferees } from '@/hooks/useReferees';
import { useDebounce } from '@/hooks/useDebounce';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import { RefereeCertification, resolveEnum } from '@/models/tournament';
import type { RefereeView, RefereeListQuery } from '@/models/tournament';
import CreateRefereeModal from './components/CreateRefereeModal';
import EditRefereeModal from './components/EditRefereeModal';
import RefereeDetailPanel from './components/RefereeDetailPanel';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

/**
 * The API serializes enums as STRINGS ("FIFAInternational"), but the label/colour maps below are
 * keyed by the NUMERIC enum. Indexing them with the raw wire value silently yields undefined, so
 * the certification badge rendered blank (or "Unknown") on every row. resolveEnum bridges both.
 */
const certOf = (v: unknown) => resolveEnum(RefereeCertification, v) as RefereeCertification;

const certificationLabels: Record<RefereeCertification, string> = {
  [RefereeCertification.NationalLevelA]: 'National Level A',
  [RefereeCertification.AFCElite]: 'AFC Elite',
  [RefereeCertification.FIFAInternational]: 'FIFA International',
};

const certificationColors: Record<RefereeCertification, string> = {
  [RefereeCertification.NationalLevelA]: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  [RefereeCertification.AFCElite]: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  [RefereeCertification.FIFAInternational]: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

export default function RefereeManagement() {
  const { t } = useTranslation('tournament');
  const { toast, showToast, dismissToast } = useToast();

  // Filter state
  const [search, setSearch] = useState('');
  const [certificationFilter, setCertificationFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReferee, setEditingReferee] = useState<RefereeView | null>(null);
  const [detailReferee, setDetailReferee] = useState<RefereeView | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  // Build query
  const query: RefereeListQuery = useMemo(() => ({
    search: debouncedSearch || undefined,
    certification: certificationFilter !== '' ? Number(certificationFilter) as RefereeCertification : undefined,
    page,
    pageSize,
  }), [debouncedSearch, certificationFilter, page, pageSize]);

  const { data, loading, error, refetch } = useReferees(query);

  // Paging re-runs the query, so `loading` goes true on EVERY page change. Gating the table on it
  // tore the whole table out and dropped a centred spinner in its place — the list vanished, the
  // page collapsed to spinner height, then snapped back. That jump is most of why paging felt bad.
  // Only the very first load (nothing to show yet) gets the spinner; later fetches keep the current
  // rows on screen and just dim them.
  const isInitialLoad = loading && !data;
  const isRefetching = loading && !!data;

  const stats = [
    { label: t('Total Officials'), value: data?.totalCount ?? 0 },
    { label: t('Available Today'), value: data?.availableCount ?? 0, color: 'text-emerald-600' },
  ];

  const existingRefereeUserIds = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((r) => r.userId);
  }, [data?.items]);

  const totalPages = data?.totalPages ?? 1;

  const handleCreateSuccess = () => {
    showToast(t('Official profile created successfully'), 'success');
    refetch();
  };

  const handleEditSuccess = () => {
    showToast(t('Official profile updated successfully'), 'success');
    refetch();
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalf = rating - fullStars >= 0.5;
    return (
      <span className="inline-flex items-center gap-0.5">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            size={14}
            className={
              i < fullStars
                ? 'text-amber-400 fill-amber-400'
                : i === fullStars && hasHalf
                  ? 'text-amber-400 fill-amber-400/50'
                  : 'text-gray-300 dark:text-gray-600'
            }
          />
        ))}
        <span className="text-xs text-muted-foreground ms-1">{rating.toFixed(1)}</span>
      </span>
    );
  };

  return (
    <>
      <PageLayout
        title={t('Match Official Management')}
        description={t('Manage official profiles, certifications, and availability')}
        icon={Users}
        accentColor="amber"
        stats={stats}
        headerActions={
          <Button
            onClick={() => setShowCreateModal(true)}
            variant="outline"
            iconName="Plus"
            data-testid="add-referee-btn"
          >
            {t('Add Official')}
          </Button>
        }
      >
        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="referee-filters">
          <div className="flex-1 min-w-0">
            <Input
              type="text"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t('Search officials by name, ID, or location...')}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              value={certificationFilter}
              onChange={(value: string) => {
                setCertificationFilter(value);
                setPage(1);
              }}
              options={[
                { label: t('All Certifications'), value: '' },
                { label: t('National Level A'), value: String(RefereeCertification.NationalLevelA) },
                { label: t('AFC Elite'), value: String(RefereeCertification.AFCElite) },
                { label: t('FIFA International'), value: String(RefereeCertification.FIFAInternational) },
              ]}
              placeholder={t('All Certifications')}
              data-testid="referee-certification-filter"
            />
          </div>
        </div>

        {/* Loading State — first load only; see isInitialLoad above. */}
        {isInitialLoad && (
          <div className="flex items-center justify-center py-16" data-testid="referee-loading">
            <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground me-2" />
            <span className="text-muted-foreground">{t('Loading data...')}</span>
          </div>
        )}

        {/* Error State */}
        {error && !isInitialLoad && (
          <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3" data-testid="referee-error">
            <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            {/* variant="outline", not class overrides: cn() is twMerge, so on the DEFAULT variant
                `text-red-700` beat `text-primary-foreground` while `bg-primary` survived unopposed
                — red text on a cobalt fill, ~1.3:1. */}
            <Button variant="outline" size="sm" onClick={refetch} className="ms-auto">
              {t('Try Again')}
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && data && data.items.length === 0 && (
          !(search || certificationFilter) ? (
            <div className="text-center py-16" data-testid="referee-empty-no-data">
              <Icon name="Users" size={48} className="mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">{t('emptyState.noRefereesYet')}</h3>
              <p className="text-sm text-muted-foreground mb-6">{t('emptyState.noRefereesYetDesc')}</p>
              <div className="flex items-center justify-center gap-3">
                <Button
                  onClick={() => setShowCreateModal(true)}
                  variant="warning"
                  data-testid="empty-add-referee-btn"
                >
                  <Plus size={16} />
                  {t('Add Official')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium"
                  data-testid="empty-promote-employee-btn"
                >
                  <Icon name="UserPlus" size={16} />
                  {t('Promote Employee to Official')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-16" data-testid="referee-empty-filters">
              <Icon name="SearchX" size={48} className="mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">{t('No officials found')}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t('Try adjusting your filters')}</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setCertificationFilter('');
                  setPage(1);
                }}
                className="px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium mx-auto"
                data-testid="referee-clear-filters-btn"
              >
                <Icon name="X" size={16} />
                {t('Clear Filters')}
              </Button>
            </div>
          )
        )}

        {/* Table */}
        {!isInitialLoad && !error && data && data.items.length > 0 && (
          <>
            <div
              className={cn(
                'bg-card border border-border rounded-lg overflow-hidden shadow-sm',
                'transition-opacity duration-200 ease-out motion-reduce:transition-none',
                isRefetching && 'opacity-60 pointer-events-none',
              )}
              aria-busy={isRefetching}
              data-testid="referee-table"
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-start text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                        {t('Name:', { nsSeparator: false })}
                      </th>
                      <th className="text-start text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                        {t('Certification')}
                      </th>
                      <th className="text-start text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                        {t('Location')}
                      </th>
                      <th className="text-start text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                        {t('Rating')}
                      </th>
                      <th className="text-start text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                        {t('Total Matches')}
                      </th>
                      <th className="text-end text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                        {t('Actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((referee) => (
                      <tr
                        key={referee.id}
                        className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                        data-testid={`referee-row-${referee.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {referee.photoUrl ? (
                              <img
                                src={referee.photoUrl}
                                alt={referee.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                                <Icon name="User" size={14} className="text-amber-600 dark:text-amber-400" />
                              </div>
                            )}
                            <span className="text-sm font-medium text-foreground">{referee.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${certificationColors[certOf(referee.certification)]}`}>
                            {t(certificationLabels[certOf(referee.certification)])}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-muted-foreground">
                            {referee.location || t('N/A')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div role="img" aria-label={t('Rating: {{rating}} out of 5', { rating: referee.rating.toFixed(1), nsSeparator: false })}>
                            {renderStars(referee.rating)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground">{referee.totalMatches}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setDetailReferee(referee)}
                              className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                              title={t('View Details')}
                              aria-label={t('View Details')}
                              data-testid={`referee-view-${referee.id}`}
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => setEditingReferee(referee)}
                              className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                              title={t('Edit')}
                              aria-label={t('Edit')}
                              data-testid={`referee-edit-${referee.id}`}
                            >
                              <Pencil size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination — `full` + totalCount + page-size to match tournament-list and
                tournament-matches. A 129-referee tenant on the old compact/10 config meant 13
                pages of blind next-next-next with no count and no way to see more at once. */}
            {data.totalCount > 0 && (
              <div data-testid="referee-pagination">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={(p) => setPage(p)}
                  variant="full"
                  showEdges
                  pageSize={pageSize}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                  totalCount={data.totalCount}
                />
              </div>
            )}
          </>
        )}
      </PageLayout>

      {/* Modals */}
      {showCreateModal && (
        <CreateRefereeModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          existingRefereeUserIds={existingRefereeUserIds}
        />
      )}

      {editingReferee && (
        <EditRefereeModal
          isOpen={!!editingReferee}
          referee={editingReferee}
          onClose={() => setEditingReferee(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      {detailReferee && (
        <RefereeDetailPanel
          referee={detailReferee}
          onClose={() => setDetailReferee(null)}
        />
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </>
  );
}
